import { CurrencyPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';

export type DrawerStatTone = 'auto' | 'positive' | 'negative' | 'neutral';

/**
 * Right-aligned money figure with a small caption, for a drawer header's trailing slot
 * (a budget's balance, a group's total). `auto` colors the amount by its sign.
 */
@Component({
  selector: 'app-drawer-stat',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CurrencyPipe],
  host: { class: 'flex flex-col items-end leading-tight' },
  template: `
    <span class="text-body-lg font-extrabold tabular-nums" [class]="amountClass()">
      @if (amount() !== null) {
        {{ amount() | currency: 'USD' : 'symbol' : '1.2-2' }}
      } @else {
        <ng-content />
      }
    </span>
    <span class="mt-0.5 text-body-xs font-semibold uppercase tracking-wider text-text-muted">{{ label() }}</span>
  `,
})
export class DrawerStatComponent {
  /** Pass `null` to project a custom figure instead of a formatted amount. */
  readonly amount = input.required<number | null>();
  readonly label = input.required<string>();
  readonly tone = input<DrawerStatTone>('auto');

  protected readonly amountClass = computed(() => {
    const tone = this.tone();
    const amount = this.amount() ?? 0;
    const resolved = tone === 'auto' ? (amount > 0 ? 'positive' : amount < 0 ? 'negative' : 'neutral') : tone;
    return TONE_CLASSES[resolved];
  });
}

const TONE_CLASSES: Record<Exclude<DrawerStatTone, 'auto'>, string> = {
  positive: 'text-amount-positive',
  negative: 'text-amount-negative',
  neutral: 'text-amount-neutral',
};
